/* Ээлжийн popup-ууд: нээх асуулга + хаахад гарах тооцооны тайлан */
import type { CwShiftReportDto } from '@/api/carwash';
import { tug } from '@/shared/format';
import { IconBank, IconBanknote, IconCard, IconCheck, IconClock, IconPlay, IconQr, IconX } from '@/shared/icons';

function timeText(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function dateText(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* «Ээлж нээгээгүй байна» — машин хүлээн авахын өмнө гарах асуулга */
export function ShiftPromptDialog({ open, busy, onOpenShift, onClose }: {
  open: boolean;
  busy: boolean;
  onOpenShift: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-[380px] bg-white rounded-[calc(var(--r)+4px)] shadow-2xl p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <IconClock size={22} />
          </span>
          <div className="flex flex-col gap-1">
            <div className="font-bold text-slate-900 text-[15px]">Ээлж нээгдээгүй байна</div>
            <div className="text-[13px] text-slate-500 leading-relaxed">
              Машин хүлээн авахын тулд эхлээд ээлжээ нээнэ үү. Ээлжийн хугацаанд хийгдсэн бүх тооцоо ээлж хаахад тайлан болж гарна.
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 min-h-[44px] rounded-[calc(var(--r)-4px)] text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition">
            Буцах
          </button>
          <button onClick={onOpenShift} disabled={busy}
            className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-[calc(var(--r)-4px)] text-sm font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95 transition disabled:opacity-60">
            <IconPlay size={15} strokeWidth={2.5} />
            {busy ? 'Нээж байна…' : 'Ээлж нээх'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* method = backend CwPaymentMethod int (Бэлэн 0, Карт 1, Шилжүүлэг 2, QPay 3) */
const PAY_META = [
  { method: 3, label: 'QPay', Icon: IconQr },
  { method: 2, label: 'Шилжүүлэг', Icon: IconBank },
  { method: 1, label: 'Карт', Icon: IconCard },
  { method: 0, label: 'Бэлэн мөнгө (кассанд байх)', Icon: IconBanknote },
];

/* Ээлж хаахад гарах тооцооны тайлан — эзэнд ойлгомжтой задаргаа */
export function ShiftReportModal({ report, onClose, title }: {
  report: CwShiftReportDto | null;
  onClose: () => void;
  title?: string;
}) {
  if (!report) return null;
  const s = report.Shift;
  return (
    <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[92%] bg-white rounded-[calc(var(--r)+4px)] shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* толгой */}
        <div className="px-5 py-4 bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] text-white flex items-center gap-3 shrink-0">
          <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <IconCheck size={20} strokeWidth={2.5} />
          </span>
          <div className="leading-tight flex-1">
            <div className="font-extrabold text-[16px]">{title ?? 'Ээлж хаагдлаа'}</div>
            <div className="text-[12px] text-cyan-50/90">
              {dateText(s.OpenedAt)} · {timeText(s.OpenedAt)} — {s.ClosedAt ? timeText(s.ClosedAt) : '—'}
              {s.OpenedBy ? ` · Касс: ${s.OpenedBy}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:bg-white/15 hover:text-white transition">
            <IconX size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          {/* Гол дүн */}
          <div className="rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 p-4 text-center">
            <div className="text-[12px] font-semibold text-slate-500">Ээлжийн нийт орлого</div>
            <div className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-tight">{tug(report.Revenue)}</div>
            <div className="text-[13px] text-slate-500 mt-1">
              <b className="text-slate-700">{report.Cars}</b> машин угаасан · дундаж чек <b className="text-slate-700">{tug(report.AvgTicket)}</b>
            </div>
          </div>

          {/* Төлбөрийн задаргаа */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Мөнгө хаана байгаа вэ</div>
            <div className="flex flex-col rounded-[var(--r)] ring-1 ring-slate-100 overflow-hidden">
              {PAY_META.map((m, idx) => {
                const row = report.ByPayment.find((p) => p.Method === m.method);
                const Icon = m.Icon;
                return (
                  <div key={m.method} className={'flex items-center gap-2.5 px-3.5 py-2.5 ' + (idx % 2 ? 'bg-slate-50/60' : 'bg-white')}>
                    <Icon size={16} className="text-slate-400 shrink-0" />
                    <span className="text-[13px] text-slate-700 flex-1">{m.label}</span>
                    <span className="text-[12px] text-slate-400">{row?.Cars ?? 0} машин</span>
                    <span className="text-[14px] font-bold text-slate-900 w-[90px] text-right">{tug(row?.Amount ?? 0)}</span>
                  </div>
                );
              })}
              {report.DiscountTotal > 0 && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-emerald-50/60">
                  <span className="text-[13px] text-emerald-700 flex-1">Нийт өгсөн хөнгөлөлт</span>
                  <span className="text-[14px] font-bold text-emerald-700">−{tug(report.DiscountTotal)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Угаагчид */}
          {report.ByWasher.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Угаагч бүрийн гүйцэтгэл</div>
              <div className="flex flex-col rounded-[var(--r)] ring-1 ring-slate-100 overflow-hidden">
                {report.ByWasher.map((w, i) => (
                  <div key={w.Name} className={'flex items-center gap-2.5 px-3.5 py-2.5 ' + (i % 2 ? 'bg-slate-50/60' : 'bg-white')}>
                    <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center shrink-0">{w.Name[0]}</span>
                    <span className="text-[13px] text-slate-700 flex-1">{w.Name}</span>
                    <span className="text-[12px] text-slate-400">{w.Cars} машин</span>
                    <span className="text-[14px] font-bold text-slate-900 w-[90px] text-right">{tug(w.Revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Үйлчилгээ */}
          {report.ByService.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Юу их зарагдсан бэ</div>
              <div className="flex flex-col rounded-[var(--r)] ring-1 ring-slate-100 overflow-hidden">
                {report.ByService.map((sv, i) => (
                  <div key={sv.Name} className={'flex items-center gap-2.5 px-3.5 py-2.5 ' + (i % 2 ? 'bg-slate-50/60' : 'bg-white')}>
                    <span className="text-[13px] text-slate-700 flex-1">{sv.Name}</span>
                    <span className="text-[12px] text-slate-400">{sv.Count}ш</span>
                    <span className="text-[14px] font-bold text-slate-900 w-[90px] text-right">{tug(sv.Revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 shrink-0">
          <button onClick={onClose}
            className="w-full min-h-[48px] rounded-[var(--r)] text-[15px] font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95 active:scale-[0.99] transition">
            Хаах
          </button>
        </div>
      </div>
    </div>
  );
}
